/**
 * Token storage for SonAuto Admin.
 *
 * ==================== THE TRADEOFF, WRITTEN DOWN ====================
 *
 * ACCESS TOKEN — held in a module-level variable, IN MEMORY ONLY.
 *   It is short-lived (see `expiresAt` from the API) and never touches
 *   `localStorage`, so an XSS payload that runs after the page has loaded
 *   cannot simply read it out of storage at leisure. A full page reload loses
 *   it, which is fine: the refresh token mints a new one.
 *
 * REFRESH TOKEN — persisted in `localStorage` under a single key.
 *   This is a REAL tradeoff and it is chosen deliberately:
 *
 *   - The ideal store is an httpOnly, Secure, SameSite=Strict cookie, which
 *     JavaScript cannot read at all. This app cannot use one: the API is a
 *     Cloudflare Worker that may be served from a DIFFERENT origin than the
 *     PWA (api.example.com vs admin.example.com), the three PWAs share one
 *     API, and an installed PWA on iOS has historically had cookies evicted by
 *     ITP after 7 days of inactivity — which would log every manager out every
 *     week. So the token is stored where the app can attach it as a header.
 *
 *   - The exposure: any script running on this origin can read the refresh
 *     token. Mitigations in place — (a) the API ROTATES the refresh token on
 *     every use and treats a reused token as theft, revoking the entire session
 *     family (`REFRESH_TOKEN_REPLAY` in apps/api-worker/src/auth/session.ts), so
 *     a stolen token is single-use and its use is detectable; (b) a strict CSP
 *     is served with the app; (c) sessions are bound to tenant + app + device
 *     and can be revoked centrally; (d) `sessionStorage` was rejected because
 *     it breaks the "reopen the installed app" flow entirely.
 *
 *   - `localStorage` is per-origin, so it is not shared with the POS or Staff
 *     app unless they are deployed on the same host. The key is namespaced by
 *     app anyway.
 *
 *   If the deployment ever puts the API on the same site as the PWAs, switch
 *   this to an httpOnly cookie and delete this file. That is the better answer.
 * ====================================================================
 */
import type { AuthTokens } from '@sonauto/api-client';

const REFRESH_KEY = 'sonauto.admin.refreshToken';
/** Remembered so a manager does not retype their tenant code every morning. */
const TENANT_KEY = 'sonauto.admin.tenantCode';

/** In-memory only — intentionally lost on reload. */
let accessToken: string | null = null;
let accessExpiresAt = 0;

export function getTokens(): AuthTokens | null {
  const refreshToken = readRefreshToken();
  if (refreshToken === null) return null;
  return {
    // An empty access token is valid state: it means "we have a refresh token
    // but no access token yet", and the client will refresh on the first 401.
    accessToken: accessToken ?? '',
    refreshToken,
    expiresAt: accessExpiresAt,
  };
}

export function setTokens(tokens: AuthTokens | null): void {
  if (tokens === null) {
    accessToken = null;
    accessExpiresAt = 0;
    safeRemove(REFRESH_KEY);
    return;
  }
  accessToken = tokens.accessToken;
  accessExpiresAt = tokens.expiresAt;
  safeWrite(REFRESH_KEY, tokens.refreshToken);
}

export function hasSession(): boolean {
  return readRefreshToken() !== null;
}

export function clearTokens(): void {
  setTokens(null);
}

export function rememberedTenantCode(): string {
  return safeRead(TENANT_KEY) ?? '';
}

export function rememberTenantCode(code: string): void {
  safeWrite(TENANT_KEY, code);
}

function readRefreshToken(): string | null {
  const value = safeRead(REFRESH_KEY);
  return value === null || value === '' ? null : value;
}

function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage disabled (private mode / quota). The session then lives only for
    // as long as the tab does, which is degraded but not broken.
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}
